/* eslint-disable react/no-unescaped-entities */
import { Meta } from '@/layout/Meta'
import { Main } from '@/templates/Main'
import { Code } from '@/utils/Code'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { note } from '@/utils/Note'
import BlogCta from './blog-cta'

const BingLink = (props: {path: string, children: string}) => {
  const relativePath = props.path.replace(/^\//, '') // strip leading slash if it exists
  const linkText = props.children
  return (
    <Link href={`https://learn.microsoft.com/en-us/advertising/${relativePath}`}>
      { linkText }
    </Link>
  )
}

const HowToIntegrateWithBingAds = () => {
  const router = useRouter()

  useEffect(() => {
    note('Visited Bing tutorial')
  }, [])
  
  return (
    <Main meta={<Meta 
        title="How to Integrate with Bing Ads"
        description="Connecting to Bing Ads Reporting API" />}>
      <h1 className="text-4xl font-bold text-sky-700">Bing Ads</h1>
      <h2>Campaign Performance Report</h2>
      <BlogCta />
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Overview</h2>
        <BingLink path="/reporting-service/reporting-service-reference">Bing Ads Reporting API</BingLink> is 
        intended to be run on a .NET system, but it is possible to connect to the API
        using other languages such as Javascript or Python.  This guide will walk you through 
        the steps to connect
        to the Bing Ads Reporting API using Typescript.
      </div>
      <div>
        The steps are:
        <ol className="list-decimal list-inside">
          <li>
            <BingLink path="/guides/authentication-oauth-register">
              Create and setup a Bing Ads developer account
            </BingLink>
          </li>
          <li>Identify your customer ID and customer account ID</li>
          <li>Generate a report</li>
          <li>Poll until report is created</li>
          <li>Download the csv</li>
          <li>Parse</li>
        </ol>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Creating the Bing Ads Developer Account</h2>
        I won't go into detail here as this guide is more about the technical implementation.  You
        will need to generate a client ID and secret.  
        
        The documentation starts <BingLink path="/guides/authentication-oauth-register">here</BingLink>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Identify your Customer ID and Customer Account ID</h2>
        You will need to identify your customer ID and customer account ID.  You can do this by
        logging into your Bing Ads account, and checking the URL.  The URL will look something like:

        <Code>
          https://ui.bingads.microsoft.com/campaigns?cid=YOUR_CUSTOMER_ID&aid=YOUR_ACCOUNT_ID
        </Code>

        <img src={`${router.basePath}/assets/images/bing-account-id-marked.png`} alt="Bing Ads Customer Account ID" />
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Generate a Report</h2>
        Now that you've got your client ID and secret, you will need to generate a SOAP request to 
        Bing to generate a report.  Microsoft supports some libraries if you're using&nbsp;
        <BingLink path="/guides/code-example-report-requests">
          C#, Python, Java, or PHP
        </BingLink>.  If you're using Javascript you'll need to generate the SOAP request manually.

        <h2 className="text-l font-bold text-sky-700 mt-4">SOAP Request XML</h2>
        The request will look something like below.
        
        <div>
          <span className="italic">You will need:</span>
          <ul className="list-disc list-inside">
            <li>Your developer account credentials</li>
            <li>Your CustomerID and CustomerAccountID</li>
            <li>
              <BingLink path="/reporting-service/campaignperformancereportcolumn">
                CampaignPerformanceReportColumn
              </BingLink> fields<br/>
            </li>
            <li>
              <BingLink path="https://learn.microsoft.com/en-us/advertising/reporting-service/reporttime?view=bingads-13">
                TimePeriod
              </BingLink>
            </li>
          </ul>
        </div>
        
        <Code>{`
          <s:Envelope xmlns:i="http://www.w3.org/2001/XMLSchema-instance" xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
          <s:Header xmlns="https://bingads.microsoft.com/Reporting/v13">
            <Action mustUnderstand="1">SubmitGenerateReport</Action>
            <AuthenticationToken i:nil="false">\${accessToken}</AuthenticationToken>
            <CustomerAccountId i:nil="false">\${accountId}</CustomerAccountId>
            <CustomerId i:nil="false">\${customerId}</CustomerId>
            <DeveloperToken i:nil="false">\${developerToken}</DeveloperToken>
          </s:Header>
          <s:Body>
            <SubmitGenerateReportRequest xmlns="https://bingads.microsoft.com/Reporting/v13">
              <ReportRequest i:nil="false" i:type="CampaignPerformanceReportRequest">
                <ExcludeColumnHeaders>false</ExcludeColumnHeaders>
                <ExcludeReportFooter>true</ExcludeReportFooter>
                <ExcludeReportHeader>true</ExcludeReportHeader>
                <ReportName i:nil="false">CampaignPerformanceReport.\${now}</ReportName>
                <Aggregation>Daily</Aggregation>
                <Columns i:nil="false">
                  <CampaignPerformanceReportColumn>AccountName</CampaignPerformanceReportColumn>
                  <CampaignPerformanceReportColumn>AccountNumber</CampaignPerformanceReportColumn>
                  ...
                </Columns>
                <Scope i:nil="false">
                  <AccountIds i:nil="false" xmlns:a1="http://schemas.microsoft.com/2003/10/Serialization/Arrays">
                    <a1:long>\${accountId}</a1:long>
                  </AccountIds>
                </Scope>
                <Time i:nil="false">
                  <PredefinedTime i:nil="false">LastSevenDays</PredefinedTime>
                  <ReportTimeZone i:nil="false">EasternTimeUSCanada</ReportTimeZone>
                </Time>
              </ReportRequest>
            </SubmitGenerateReportRequest>
          </s:Body>
        </s:Envelope>
        `}</Code>

        <h2 className="text-l font-bold text-sky-700 mt-4">Javascript Fetch Request</h2>
        Now plug that SOAP request in to a fetch request:
        <Code>{`
          // Call the reporting API
          const resp = await fetch(
            "https://reporting.api.bingads.microsoft.com/Api/Advertiser/Reporting/V13/ReportingService.svc",
            {
              method: "post",
              headers: {
                "Content-Type": "text/xml",
                SOAPAction: "SubmitGenerateReport",
              },
              body: \`\${soapRequest}\`,
            }
          );

          // A ReportRequestId element will be returned if the report was generated successfully
          const respText = await resp.text()
          const reportRequestParsed = respText.match(/<ReportRequestId>([0-9_]+)<\/ReportRequestId>/)

          if (!reportRequestParsed) {
            throw new Error('Error generating report request')
          }
        `}</Code>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Polling for Report Status</h2>
        Now that you've requested the report to be build, you have to wait for it to be created.  You can 
        check the status of the report by calling the PollGenerateReport endpoint.

        <h2 className="text-l font-bold text-sky-700 mt-4">SOAP Request XML</h2>
        You will need the report request ID from the previous step.
        <Code>{`
        <s:Envelope xmlns:i="http://www.w3.org/2001/XMLSchema-instance" xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
            <s:Header xmlns="https://bingads.microsoft.com/Reporting/v13">
            <Action mustUnderstand="1">PollGenerateReport</Action>
            <AuthenticationToken i:nil="false">\${accessToken}</AuthenticationToken>
            <CustomerAccountId i:nil="false">\${accountId}</CustomerAccountId>
            <CustomerId i:nil="false">\${customerId}</CustomerId>
            <DeveloperToken i:nil="false">\${developerToken}</DeveloperToken>
            </s:Header>
            <s:Body>
            <PollGenerateReportRequest xmlns="https://bingads.microsoft.com/Reporting/v13">
                <ReportRequestId i:nil="false">\${reportRequestId}</ReportRequestId>
            </PollGenerateReportRequest>
            </s:Body>
        </s:Envelope>
        `}</Code>

        <h2 className="text-l font-bold text-sky-700 mt-4">Javascript Fetch Request</h2>
        Five seconds is a good interval to check the status of the report.

        <Code>{`
        let status = 'Pending'
        while (status === 'Pending') {
            await sleep(5) // Wait five seconds
    
            // Call PollGenerateReport service
            const resp = await fetch('https://reporting.api.bingads.microsoft.com/Api/Advertiser/Reporting/V13/ReportingService.svc', {
                method: 'post',
                headers: {
                    'Content-Type': 'text/xml',
                    'SOAPAction': 'PollGenerateReport',
                },
                body: \`\${soapRequest}\`,
            })
        
            // Parse response text for the status
            const respText = await resp.text()
            status = respText.match(/<Status>(.+)<\/Status>/)?.[1]!
    
            // Status will be Success, Error, or Pending
            if (status === 'Success') {
                // Extract the download URL for the next step
                const encodedUrl = respText.match(/<ReportDownloadUrl>(.+)<\/ReportDownloadUrl>/)?.[1]
                const url = encodedUrl?.replace(/&amp;/g, '&')
        
                return url
            }
    
            if (status === 'Error') {
              throw new Error('Error polling for report')
            }
        }
        `}</Code>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Downloading the Report</h2>
        Now that the report has been generated, you can download it.  The URL will be returned 
        in the previous step.

        <p>
          Use a standard fetch to download the report.  It will 
          need to be unzipped before parsing as a CSV.
        </p>

        <p>
          A final note for parsing - the CSV header row always contains a non-printable character
          as the first character.  It an be removed with a regex replace.
        </p>
        <Code>{`
          return new Promise((resolve, reject) => {
            fs.createReadStream(path)
              .pipe(csv()) // "csv" is the "csv-parser" package
              .on("data", (row) => {
                // The first key always has a non printable character in it
                const firstKey = Object.keys(row)[0]!
                const firstVal = row[firstKey]
                const firstKeyFixed = firstKey.replace(/[^A-Za-z]/g, '')
        
                delete row[firstKey]
                row[firstKeyFixed] = firstVal
        
                data.push(row)
              })
              .on("end", () => {
                  resolve(data)
              })
              .on("error", e => reject(e))
          }
        `}</Code>
      </div>
    </Main>
  )
}

export default HowToIntegrateWithBingAds
